import { AuditType, IAuditConfig, InformativeCheckType } from '../data/audits';

/** Client-only publish diff audits — not in audit_configs / not profile-managed. */
export const CLIENT_ONLY_CONTENT_DIFF_AUDIT_SLUGS = new Set([
  'CONTENT_DIFF_AUDIT',
  'PUBLISHED_CONTENT_DIFF_AUDIT',
]);

/** Audits that participate in profile assignment, catalog sweep, and Audit Library toggles. */
export const isProfileManagedAudit = (audit: IAuditConfig): boolean =>
  !audit.isContentDiff && !CLIENT_ONLY_CONTENT_DIFF_AUDIT_SLUGS.has(audit.slug);

export const filterProfileManagedAudits = (audits: IAuditConfig[]): IAuditConfig[] =>
  audits.filter(isProfileManagedAudit);

/**
 * Psych-team validation stays assigned under warnings-only defaults (unsaved profile).
 * Other blocking audits remain opt-in via Audits Selection.
 */
export const AUDIT_TYPES_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT: ReadonlySet<AuditType> = new Set(['PSYCH']);

/**
 * Seed audits that stay assigned regardless of audit type. These close a gap a saved profile
 * cannot know about yet, so opting in per profile would leave the gap open on every existing
 * assignment row until an author edits it.
 */
export const AUDIT_SLUGS_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT: ReadonlySet<string> = new Set([
  'FT_PLACEMENT_AUDIT',
]);

export const isExemptFromWarningsOnlyDefault = (audit: IAuditConfig): boolean =>
  AUDIT_SLUGS_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT.has(audit.slug)
  || (!!audit.auditType && AUDIT_TYPES_EXEMPT_FROM_WARNINGS_ONLY_DEFAULT.has(audit.auditType));

export interface ExcludedChecks {
  [checkId: string]: boolean;
}

export interface ExcludedAuditEntry {
  isExcluded: boolean;
  excludedChecks: ExcludedChecks;
}

export type ExcludedAuditsMap = { [slug: string]: ExcludedAuditEntry };

export const getDefaultExcludedAuditEntry = (): ExcludedAuditEntry => {
  return { isExcluded: false, excludedChecks: {} };
};

const isStaticWarningCheck = (
  checkInformative?: InformativeCheckType,
  auditInformative?: InformativeCheckType,
): boolean => {
  const value = checkInformative ?? auditInformative;
  return value !== undefined && value !== false && value !== null;
};

/**
 * Default audit profile assignment: enable informative (warning) checks only;
 * exclude blocking audits and sub-checks when no saved profile config exists.
 */
export const buildWarningsOnlyDefaultExcludedConfig = (
  audits: IAuditConfig[],
): ExcludedAuditsMap => {
  const config: ExcludedAuditsMap = {};

  for (const audit of filterProfileManagedAudits(audits)) {
    if (audit.isDisabled || audit.isHidden) {
      continue;
    }

    if (isExemptFromWarningsOnlyDefault(audit)) {
      continue;
    }

    const entry: ExcludedAuditEntry = getDefaultExcludedAuditEntry();
    const checks = audit.checks?.filter((check) => !!check.caption && check.id) ?? [];

    if (!checks.length) {
      config[audit.slug] = { isExcluded: true, excludedChecks: {} };
      continue;
    }

    let hasWarningCheck = false;
    for (const check of checks) {
      if (
        isStaticWarningCheck(
          check.informativeOnly,
          audit.commonCheckProps?.informativeOnly,
        )
      ) {
        hasWarningCheck = true;
      } else {
        entry.excludedChecks[check.id] = true;
      }
    }

    entry.isExcluded = !hasWarningCheck;
    config[audit.slug] = entry;
  }

  return config;
};

export const mergeExcludedConfigWithWarningsDefault = (
  stored: ExcludedAuditsMap | null | undefined,
  audits: IAuditConfig[],
): ExcludedAuditsMap => {
  if (stored && Object.keys(stored).length) {
    return stored;
  }
  return buildWarningsOnlyDefaultExcludedConfig(audits);
};

/**
 * Saved profile map + catalog sweep: fill missing profile-managed slugs/checks with
 * warnings-only defaults. Client-only publish diff audits are out of scope.
 * (Feature 11 — wire from mergeExcludedConfigWithWarningsDefault when `-2` ships.)
 */
export const mergeStoredConfigWithCatalog = (
  stored: ExcludedAuditsMap,
  audits: IAuditConfig[],
): ExcludedAuditsMap => {
  const profileAudits = filterProfileManagedAudits(audits);
  const warningsDefault = buildWarningsOnlyDefaultExcludedConfig(profileAudits);
  const merged: ExcludedAuditsMap = JSON.parse(JSON.stringify(stored));

  for (const audit of profileAudits) {
    const slug = audit.slug;
    const defaultEntry = warningsDefault[slug] ?? getDefaultExcludedAuditEntry();
    const storedEntry = merged[slug];

    if (!storedEntry) {
      merged[slug] = JSON.parse(JSON.stringify(defaultEntry));
      continue;
    }

    if (!storedEntry.excludedChecks) {
      storedEntry.excludedChecks = {};
    }

    for (const check of audit.checks?.filter((c) => !!c.caption && c.id) ?? []) {
      if (storedEntry.excludedChecks[check.id] === undefined) {
        storedEntry.excludedChecks[check.id] = !!defaultEntry.excludedChecks[check.id];
      }
    }
  }

  return merged;
};

/** Mirrors public/test-auth/audit-config-impact classifyEnablement. */
export type AuditProfileEnablement = 'enabled' | 'partial' | 'excluded' | 'default-unsaved';

export const classifyAuditProfileEnablement = (
  config: ExcludedAuditsMap | null | undefined,
  slug: string,
): AuditProfileEnablement => {
  if (!config) { return 'default-unsaved'; }
  const entry = config[slug];
  if (!entry) { return 'enabled'; }
  if (entry.isExcluded) { return 'excluded'; }
  if (Object.values(entry.excludedChecks ?? {}).some(Boolean)) { return 'partial'; }
  return 'enabled';
};

export const isAuditUsedOnProfile = (enablement: AuditProfileEnablement): boolean =>
  enablement !== 'excluded';
